import { useState } from 'react';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { Ship, Package, MapPin, Calendar, FileText, Loader2, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const cargoTypes = [
  'General Cargo',
  'Containerized Goods',
  'Bulk Commodities',
  'Refrigerated Goods',
  'Machinery & Equipment',
  'Vehicles',
  'Hazardous Materials',
  'Project Cargo',
  'Other',
];

export default function QuotePage() {
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    origin_port: '',
    destination_port: '',
    cargo_type: '',
    weight: '',
    dimensions: '',
    special_requirements: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (value) => {
    setFormData({ ...formData, cargo_type: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/quotes`, formData);
      setSubmitted(true);
      toast.success('Quote request submitted successfully!');
    } catch (error) {
      toast.error('Failed to submit quote request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="quote-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative py-32 lg:py-40 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_maritime-connect-2/artifacts/eh576k8r_Copilot_20251106_002554.png')`,
          }}
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="rsl-container relative z-10">
          <span className="text-safety-orange font-mono text-xs tracking-widest uppercase">
            Free Estimate
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black text-white uppercase tracking-tight mt-2">
            Get a Quote
          </h1>
          <p className="text-white/80 text-lg max-w-xl mt-6">
            Tell us about your shipping needs and our team will provide a customized quote within 24 hours.
          </p>
        </div>
      </section>

      {/* Quote Form Section */}
      <section className="py-20 lg:py-32">
        <div className="rsl-container">
          <div className="max-w-4xl mx-auto">
            {submitted ? (
              <div className="text-center py-16 bg-slate-50 px-8" data-testid="quote-success">
                <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
                <h2 className="text-3xl font-black text-navy uppercase mb-4">Quote Request Received!</h2>
                <p className="text-slate-600 text-lg mb-8 max-w-md mx-auto">
                  Thank you for your inquiry. Our logistics team will review your requirements and send you a detailed quote within 24 hours.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Button
                    onClick={() => {
                      setSubmitted(false);
                      setFormData({
                        company_name: '',
                        contact_name: '',
                        email: '',
                        phone: '',
                        origin_port: '',
                        destination_port: '',
                        cargo_type: '',
                        weight: '',
                        dimensions: '',
                        special_requirements: '',
                      });
                    }}
                    className="bg-safety-orange hover:bg-orange-600 text-white font-bold uppercase tracking-widest text-sm h-12 px-8"
                  >
                    Submit Another Quote
                  </Button>
                  <Button
                    variant="outline"
                    className="border-2 border-navy text-navy hover:bg-navy hover:text-white font-bold uppercase tracking-widest text-sm h-12 px-8"
                    onClick={() => window.location.href = '/tracking'}
                  >
                    Track a Shipment
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-slate-50 p-8 lg:p-12" data-testid="quote-form">
                {/* Contact Information */}
                <div className="mb-10">
                  <h2 className="text-xl font-bold text-navy mb-6 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-safety-orange" />
                    Contact Information
                  </h2>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block font-mono text-xs uppercase tracking-wider text-slate-500 mb-2">
                        Company Name *
                      </label>
                      <Input
                        type="text"
                        name="company_name"
                        value={formData.company_name}
                        onChange={handleChange}
                        required
                        className="h-12 border-2 border-slate-200 focus:border-safety-orange"
                        placeholder="Your Company Ltd."
                        data-testid="quote-company-input"
                      />
                    </div>
                    <div>
                      <label className="block font-mono text-xs uppercase tracking-wider text-slate-500 mb-2">
                        Contact Name *
                      </label>
                      <Input
                        type="text"
                        name="contact_name"
                        value={formData.contact_name}
                        onChange={handleChange}
                        required
                        className="h-12 border-2 border-slate-200 focus:border-safety-orange"
                        placeholder="John Doe"
                        data-testid="quote-contact-input"
                      />
                    </div>
                    <div>
                      <label className="block font-mono text-xs uppercase tracking-wider text-slate-500 mb-2">
                        Email Address *
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="h-12 border-2 border-slate-200 focus:border-safety-orange"
                        placeholder="john@company.com"
                        data-testid="quote-email-input"
                      />
                    </div>
                    <div>
                      <label className="block font-mono text-xs uppercase tracking-wider text-slate-500 mb-2">
                        Phone Number *
                      </label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="h-12 border-2 border-slate-200 focus:border-safety-orange"
                        placeholder="+1 (555) 000-0000"
                        data-testid="quote-phone-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Shipment Details */}
                <div className="mb-10">
                  <h2 className="text-xl font-bold text-navy mb-6 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-safety-orange" />
                    Shipment Route
                  </h2>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block font-mono text-xs uppercase tracking-wider text-slate-500 mb-2">
                        Origin Port/City *
                      </label>
                      <Input
                        type="text"
                        name="origin_port"
                        value={formData.origin_port}
                        onChange={handleChange}
                        required
                        className="h-12 border-2 border-slate-200 focus:border-safety-orange"
                        placeholder="Sydney, Australia"
                        data-testid="quote-origin-input"
                      />
                    </div>
                    <div>
                      <label className="block font-mono text-xs uppercase tracking-wider text-slate-500 mb-2">
                        Destination Port/City *
                      </label>
                      <Input
                        type="text"
                        name="destination_port"
                        value={formData.destination_port}
                        onChange={handleChange}
                        required
                        className="h-12 border-2 border-slate-200 focus:border-safety-orange"
                        placeholder="Los Angeles, USA"
                        data-testid="quote-destination-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Cargo Details */}
                <div className="mb-10">
                  <h2 className="text-xl font-bold text-navy mb-6 flex items-center gap-2">
                    <Package className="w-5 h-5 text-safety-orange" />
                    Cargo Details
                  </h2>
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block font-mono text-xs uppercase tracking-wider text-slate-500 mb-2">
                        Cargo Type *
                      </label>
                      <Select onValueChange={handleSelectChange} required>
                        <SelectTrigger className="h-12 border-2 border-slate-200 focus:border-safety-orange" data-testid="quote-cargo-select">
                          <SelectValue placeholder="Select cargo type" />
                        </SelectTrigger>
                        <SelectContent>
                          {cargoTypes.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block font-mono text-xs uppercase tracking-wider text-slate-500 mb-2">
                        Estimated Weight *
                      </label>
                      <Input
                        type="text"
                        name="weight"
                        value={formData.weight}
                        onChange={handleChange}
                        required
                        className="h-12 border-2 border-slate-200 focus:border-safety-orange"
                        placeholder="e.g., 5,000 kg or 20 TEU"
                        data-testid="quote-weight-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block font-mono text-xs uppercase tracking-wider text-slate-500 mb-2">
                      Dimensions (Optional)
                    </label>
                    <Input
                      type="text"
                      name="dimensions"
                      value={formData.dimensions}
                      onChange={handleChange}
                      className="h-12 border-2 border-slate-200 focus:border-safety-orange"
                      placeholder="e.g., 10m x 3m x 2.5m"
                      data-testid="quote-dimensions-input"
                    />
                  </div>
                </div>

                {/* Special Requirements */}
                <div className="mb-10">
                  <label className="block font-mono text-xs uppercase tracking-wider text-slate-500 mb-2">
                    Special Requirements (Optional)
                  </label>
                  <Textarea
                    name="special_requirements"
                    value={formData.special_requirements}
                    onChange={handleChange}
                    rows={4}
                    className="border-2 border-slate-200 focus:border-safety-orange resize-none"
                    placeholder="Temperature control, hazardous materials handling, special handling instructions, preferred shipping dates..."
                    data-testid="quote-requirements-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-14 bg-safety-orange hover:bg-orange-600 text-white font-bold uppercase tracking-widest text-sm"
                  data-testid="quote-submit-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Request Quote
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>

                <p className="text-slate-500 text-sm text-center mt-6">
                  By submitting this form, you agree to our privacy policy. We'll respond within 24 hours.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
